/**
 * "Archive project": the confirmation that says what archiving does (hidden for everyone, services stopped, nothing
 * deleted, can be unarchived), then each project archived and stopped (sandbox/archiveRun archiveAndStop), one after
 * the other. `onDone({ archived, failed })` with the ids archived and `[{ id, name, error }]` that were not.
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ConfirmDialog from '../ConfirmDialog';
import './ArchivedProjects.css';
import { ARCHIVE_EFFECTS, archiveAndStop } from '../sandbox/archiveRun';

export default function ArchiveDialog({ projects, onCancel, onDone }) {
  const [busy, setBusy] = useState(false);
  const open = Array.isArray(projects) && projects.length > 0;
  const one = open && projects.length === 1;
  const confirm = async() => {
    setBusy(true);
    const archived = [];
    const failed = [];
    for (const project of projects) {
      try {
        // eslint-disable-next-line no-await-in-loop -- one at a time: each stops its own services
        const answer = await archiveAndStop(project.id);
        archived.push(project.id);
        if (answer.stopError) { failed.push({ ...project, error: `Archived, but ${answer.stopError}` }); }
      } catch (error) {
        failed.push({ ...project, error: error.message || 'It could not be archived' });
      }
    }
    setBusy(false);
    onDone({ archived, failed });
  };
  return (
    <ConfirmDialog
      busy={busy}
      busyText="Archiving…"
      confirmText="Archive"
      isOpen={open}
      message={(
        <span className="archive-dialog-message">
          {one ? `Archive "${projects[0].name || projects[0].id}"?` : `Archive ${open ? projects.length : 0} projects?`}
          {/* Spans, not a list: ConfirmDialog's message is a paragraph. */}
          {ARCHIVE_EFFECTS.map((line) => <span className="archive-dialog-effect" key={line}>{line}</span>)}
        </span>
      )}
      onCancel={busy ? () => {} : onCancel}
      onConfirm={confirm}
      title={one ? 'Archive project' : 'Archive projects'}
    />
  );
}

ArchiveDialog.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  // The projects to archive ([{ id, name }]); none or null: closed.
  projects: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string })),
};
